import { type ReactNode } from "react";
import clsx from "clsx";

export function Card({ className, children, padded = true }: { className?: string; children: ReactNode; padded?: boolean }) {
  return (
    <div className={clsx(
      "rounded-lg border border-border bg-surface-1 shadow-card",
      padded && "p-4",
      className,
    )}>{children}</div>
  );
}

export function CardHeader({ title, subtitle, action }: { title: ReactNode; subtitle?: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-fg">{title}</h3>
        {subtitle && <p className="mt-0.5 text-xs text-fg-muted">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx("skeleton", className)} />;
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 py-8 text-center">
      <div className="text-sm text-fg-muted">{title}</div>
      {hint && <div className="text-xs text-fg-subtle">{hint}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : "Something went wrong";
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
      <div className="text-sm text-sev-high">{msg}</div>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded border border-border-strong px-2 py-1 text-xs text-fg-muted hover:border-fg-muted hover:text-fg"
        >Retry</button>
      )}
    </div>
  );
}

export function Button({
  variant = "default",
  size = "md",
  className,
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: "default" | "ghost" | "primary"; size?: "sm" | "md" }) {
  return (
    <button
      {...rest}
      className={clsx(
        "inline-flex items-center justify-center gap-1.5 rounded-md border font-medium transition-colors duration-150 ease-out-soft disabled:opacity-50 disabled:pointer-events-none",
        size === "sm" ? "h-7 px-2 text-xs" : "h-8 px-3 text-sm",
        variant === "default" && "border-border-strong bg-surface-2 text-fg hover:bg-surface-3",
        variant === "ghost" && "border-transparent text-fg-muted hover:bg-surface-2 hover:text-fg",
        variant === "primary" && "border-accent/40 bg-accent/15 text-fg hover:bg-accent/25",
        className,
      )}
    >{children}</button>
  );
}

export function Badge({
  tone = "neutral",
  className,
  children,
}: { tone?: "neutral" | "low" | "watch" | "suspicious" | "high" | "good"; className?: string; children: ReactNode }) {
  const map: Record<string, string> = {
    neutral: "border-border-strong bg-surface-2 text-fg-muted",
    low: "border-sev-low/40 bg-sev-low/15 text-sev-low",
    watch: "border-sev-watch/40 bg-sev-watch/15 text-sev-watch",
    suspicious: "border-sev-suspicious/40 bg-sev-suspicious/15 text-sev-suspicious",
    high: "border-sev-high/40 bg-sev-high/15 text-sev-high",
    good: "border-sev-good/40 bg-sev-good/15 text-sev-good",
  };
  return (
    <span className={clsx(
      "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide leading-none",
      map[tone],
      className,
    )}>{children}</span>
  );
}
